import get from 'lodash/get';

import CCTerminal from 'app/src/data/models/CCTerminal';
import GatewayConfig from 'app/src/data/models/GatewayConfig';
import BluetoothTerminal from 'app/src/payments/terminals/BluetoothTerminal';
import CayanGeniusTerminal from 'app/src/payments/terminals/CayanGeniusTerminal';
import CloudStripeTerminal from 'app/src/payments/terminals/CloudStripeTerminal';
import LightspeedPaymentsTerminal from 'app/src/payments/terminals/LightspeedPaymentsTerminal';
import LocalStripeTerminal from 'app/src/payments/terminals/LocalStripeTerminal';
import TyroTerminal from 'app/src/payments/terminals/TyroTerminal';
import VantivBlacklineTerminal from 'app/src/payments/terminals/VantivBlacklineTerminal';
import VantivTriPOSTerminal from 'app/src/payments/terminals/VantivTriPOSTerminal';

/**
 * Definition of the TerminalFactory class.  It provides an interface to construct
 * concrete terminal implementations.
 */
export default class TerminalFactory {
  /**
   * Factory method to generate a terminal of the appropriate type
   *
   * @param gatewayConfigModel {GatewayConfig} The gateway
   * @param terminalModel {CCTerminal} A model for the terminal
   *
   * @return {*}  A terminal of the appropriate type
   */
  static createTerminal(gatewayConfigModel, terminalModel) {
    let terminal = null;
    const gatewayType = gatewayConfigModel?.get('gatewayID');
    if (gatewayType && terminalModel) {
      switch (gatewayType) {
        case GatewayConfig.GatewayType.CAYAN:
          if (
            [
              CCTerminal.TerminalType.GENIUS_MX915,
              CCTerminal.TerminalType.GENIUS_P400,
              CCTerminal.TerminalType.GENIUS_E285,
              CCTerminal.TerminalType.GENIUS_M400,
            ].includes(terminalModel.get('type'))
          ) {
            terminal = new CayanGeniusTerminal(terminalModel);
          }
          break;
        case GatewayConfig.GatewayType.LIGHTSPEED_PAYMENTS:
          terminal = this.createLightspeedPaymentsTerminal(terminalModel, gatewayConfigModel);
          break;
        case GatewayConfig.GatewayType.FIRST_DATA:
        case GatewayConfig.GatewayType.VANTIV_CANADA:
        case GatewayConfig.GatewayType.VANTIV_US:
          if (terminalModel.get('type') === CCTerminal.TerminalType.BLACKLINE) {
            terminal = new VantivBlacklineTerminal(terminalModel);
          }
          break;
        case GatewayConfig.GatewayType.VANTIV_TRIPOS:
          if (terminalModel.get('type') === CCTerminal.TerminalType.TRIPOS) {
            terminal = new VantivTriPOSTerminal(terminalModel);
          }
          break;
        case GatewayConfig.GatewayType.TYRO:
          if (
            terminalModel.get('type') === CCTerminal.TerminalType.YOXIMO ||
            terminalModel.get('type') === CCTerminal.TerminalType.YOMANI
          ) {
            terminal = new TyroTerminal(terminalModel);
          }
          break;
        default:
          break;
      }
    }

    return terminal;
  }

  static createLightspeedPaymentsTerminal(terminalModel, gatewayConfigModel) {
    const terminalType = terminalModel.get('type');
    switch (terminalType) {
      case CCTerminal.TerminalType.LIGHTSPEED:
      case CCTerminal.TerminalType.LIGHTSPEED_E285_ADYEN:
      case CCTerminal.TerminalType.LIGHTSPEED_M400_ADYEN:
      case CCTerminal.TerminalType.LIGHTSPEED_P400_PLUS_ADYEN:
      case CCTerminal.TerminalType.LIGHTSPEED_V400M_ADYEN:
        return new LightspeedPaymentsTerminal(terminalModel);
      case CCTerminal.TerminalType.LIGHTSPEED_P400_PLUS_STRIPE:
        return new LocalStripeTerminal(terminalModel);
      case CCTerminal.TerminalType.LIGHTSPEED_BBPOS_WISEPOSE_STRIPE:
      case CCTerminal.TerminalType.LIGHTSPEED_STRIPE_READER_S700_STRIPE:
      case CCTerminal.TerminalType.LIGHTSPEED_STRIPE_READER_S710_STRIPE:
        const isPlatformVersionSupported =
          get(gatewayConfigModel, 'attributes.parameters.platformVersion', undefined) ===
          GatewayConfig.PLATFORM_VERSIONS.v3;

        return isPlatformVersionSupported
          ? new CloudStripeTerminal(terminalModel)
          : new LocalStripeTerminal(terminalModel);
      case CCTerminal.TerminalType.LIGHTSPEED_BBPOS_CHIPPER:
      case CCTerminal.TerminalType.LIGHTSPEED_BBPOS_WISEPAD_3_STRIPE:
      case CCTerminal.TerminalType.LIGHTSPEED_STRIPE_READER_M2_STRIPE:
        // Dummy terminal class for Stencil
        return new BluetoothTerminal(terminalModel);
      default:
        return null;
    }
  }
}
